import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client/react';
import { DateTime } from 'luxon';

import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { CertificateInfo } from 'training/components/CertificateInfo';
import { DatePicker } from 'common/components/FormField';
import {
    ICertificateWithScoringFragment,
    CreateCertificateForEnrollmentsDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';

interface IProps {
    certificate?: ICertificateWithScoringFragment | null;
    enrollmentIds: string[];
    hasExternalCertificates?: boolean;
    open: boolean;
    onClose: () => void;
    onConfirm: (issueDate?: DateTime) => void;
}

export const CreateEnrollmentCertificateDialog = ({
    certificate,
    enrollmentIds,
    hasExternalCertificates,
    open,
    onClose,
    onConfirm,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const [createCertificates, { loading: createCertificatesLoading }] =
        useMutation(CreateCertificateForEnrollmentsDocument);
    const { showApolloError } = useApolloError();
    // Use state to avoid text changing when dialog is closed
    const [enrollmentCount, setEnrollmentCount] = useState<number>(
        enrollmentIds.length
    );

    useEffect(() => {
        if (!open) return;

        setEnrollmentCount(enrollmentIds.length);
    }, [enrollmentIds, open]);

    const handleCreateCertificates = async (issueDate?: DateTime | null) => {
        try {
            await createCertificates({
                variables: {
                    offerEventEnrollmentIds: enrollmentIds,
                    issueDate: issueDate?.toISO(),
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('createCertificateEnrollmentsDialog.success', {
                count: enrollmentIds.length || 0,
            }),
            {
                variant: 'success',
            }
        );

        onConfirm();

        return;
    };

    const translateKey = 'createCertificateEnrollmentsDialog';

    const dateToday = useMemo(() => DateTime.now(), [open]);

    const initialValues: { issueDate: DateTime | null } = {
        issueDate: dateToday,
    };

    const validationSchema = Yup.object().shape({
        issueDate: Yup.object().nullable(),
    });

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, actions) => {
                await handleCreateCertificates(values.issueDate);

                actions.resetForm();
            }}
        >
            {({ submitForm, isSubmitting }) => (
                <ConfirmDialog
                    fullWidth
                    confirmText={translate(`${translateKey}.confirmText`)}
                    loading={isSubmitting || createCertificatesLoading}
                    maxWidth="sm"
                    open={open}
                    title={translate(`${translateKey}.title`)}
                    onCancel={onClose}
                    onClose={onClose}
                    onConfirm={submitForm}
                >
                    <Typography sx={{ mb: 1 }}>
                        {translate(`${translateKey}.text`, {
                            count: enrollmentCount,
                        })}
                    </Typography>

                    {hasExternalCertificates && (
                        <Typography sx={{ my: 1, color: 'error.main' }}>
                            {translate(
                                `${translateKey}.externalCertificateInfo`
                            )}
                        </Typography>
                    )}

                    {!!certificate && (
                        <Box sx={{ my: 3 }}>
                            <CertificateInfo certificate={certificate} />
                        </Box>
                    )}

                    <Field
                        component={DatePicker}
                        label={translate('certificateIssueDate')}
                        margin="dense"
                        name="issueDate"
                    />
                </ConfirmDialog>
            )}
        </Formik>
    );
};
